// =============================================================================
// PTEL - AdminPanel v5
// Panel de gestión de usuarios con filtros, búsqueda y dropdown custom
// Iconos Lucide (PTEL Design System), 3 roles sin INE
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  ShieldCheck, Pencil, Eye, AlertTriangle, Search, X,
  Mail, Users, ChevronDown,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAudit } from '@/hooks/useAudit';
import { useIsMounted } from '@/hooks/useIsMounted';
import { listUsers, updateUserRole } from '@/services/firebase/admin.service';
import { listPendingRequests, approveRequest, rejectRequest } from '@/services/firebase/editorRequest.service';
import { PendingRequests } from './PendingRequests';
import { AuditLogViewer } from './AuditLogViewer';
import type { FirebaseUser, UserRole } from '@/types/firebase.types';
import { ROLE_DURATIONS, calculateExpirationDate as _calculateExpirationDate, type RoleDuration, type EditorRequest } from '@/types/roles';
import type { Theme } from '@/components/auth/auth.types';
import { getAuthTokens, type AuthTokenSet } from '@/components/auth/auth.tokens';

// =============================================================================
// Constants & Config
// =============================================================================

interface AdminPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theme?: Theme;
}

type FilterRole = UserRole | 'all';

interface RoleConfig {
  label: string;
  color: string;
  icon: typeof ShieldCheck;
}

const ROLE_CONFIG: Record<UserRole, RoleConfig> = {
  admin:     { label: 'Admin',      color: '#f59e0b', icon: ShieldCheck },
  moderator: { label: 'Moderador',  color: '#8b5cf6', icon: ShieldCheck },
  editor:    { label: 'Editor',     color: '#3b82f6', icon: Pencil },
  viewer:    { label: 'Visor',      color: '#a8a8a4', icon: Eye },
};

const ROLE_TOOLTIPS: Record<UserRole | 'all', string> = {
  all:       'Mostrar todos los usuarios sin filtrar por rol',
  admin:     'Administradores con acceso completo al panel y a la auditoría',
  moderator: 'Moderadores que aprueban o rechazan propuestas',
  editor:    'Editores que crean y modifican contenido sujeto a revisión',
  viewer:    'Visores: usuarios con acceso solo de consulta',
};

const ROLES: UserRole[] = ['admin', 'moderator', 'editor', 'viewer'];
const EDITOR_DOMAIN = '@juntadeandalucia.es';

// =============================================================================
// PANADM-b1: fallos de carga con su código (maqueta: «Motivo … (código)»)
// =============================================================================

interface LoadError {
  message: string;
  code: string;
}

/**
 * El código de Firebase (o el de `AdminListError`) va aparte; el texto es siempre
 * en español: el de `AdminListError` o el propio de la lista (Firebase lo da en inglés).
 */
function toLoadError(err: unknown, fallbackMessage: string): LoadError {
  const code = (err as { code?: unknown } | null)?.code;
  return {
    message: err instanceof Error && err.name === 'AdminListError' ? err.message : fallbackMessage,
    code: typeof code === 'string' && code ? code : 'unknown',
  };
}

/**
 * Aviso de fallo de una lista: motivo, código y «Reintentar». «No significa que
 * no haya»: la lista no se ha podido leer (maqueta PANADM).
 */
function LoadErrorNotice({ testId, error, what, onRetry, t }: {
  testId: string; error: LoadError; what: string; onRetry: () => void; t: AuthTokenSet;
}) {
  return (
    <div data-testid={testId} role="alert" style={{
      padding: '12px', background: t.errorBg,
      border: `1px solid ${t.error}30`, borderRadius: '8px',
      color: t.error, fontSize: '13px', marginBottom: '12px',
    }}>
      <div>
        {error.message} Motivo: <span data-testid={`${testId}-code`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{error.code}</span>.
        {' '}No significa que no haya {what}: no se ha podido leer.
      </div>
      <button
        type="button"
        data-testid={`${testId}-retry`}
        onClick={onRetry}
        style={{
          marginTop: '8px', padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
          background: 'transparent', border: `1px solid ${t.error}`, color: t.error,
          fontSize: '12px', fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        Reintentar
      </button>
    </div>
  );
}

/** Ámbar de aviso (sin ficha en `AuthTokenSet`); legible en tema claro y oscuro. */
const AMBER = '#d97706';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * PANADM-b1: caducidad real del rol, como la maqueta de PANADM:
 * vencida en rojo, ámbar a 30 días o menos con los días que quedan, y
 * «Sin caducidad» cuando la ficha no fija fecha. El visor no caduca.
 */
export function describeExpiry(
  role: UserRole,
  expiresAt: Date | null | undefined,
  now: Date = new Date(),
): { text: string; tone: 'muted' | 'warn' | 'error' } | null {
  if (role === 'viewer') return null;
  if (!expiresAt) return { text: 'Sin caducidad', tone: 'muted' };
  const fecha = expiresAt.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  if (expiresAt.getTime() < now.getTime()) return { text: `Vencida el ${fecha}`, tone: 'error' };
  // Días de calendario (medianoche local a medianoche local), no milisegundos:
  // así el cambio de hora no suma ni resta un día.
  const medianoche = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dias = Math.round((medianoche(expiresAt) - medianoche(now)) / DAY_MS);
  if (dias === 0) return { text: `${fecha} · caduca hoy`, tone: 'warn' };
  if (dias <= 30) return { text: `${fecha} · quedan ${dias} ${dias === 1 ? 'día' : 'días'}`, tone: 'warn' };
  return { text: `Caduca el ${fecha}`, tone: 'muted' };
}

// =============================================================================
// Hook: useAdminUsers
// =============================================================================

/**
 * SEC0-b2c-2: carga solo con `enabled` (panel abierto por una administradora).
 * Antes cargaba al montar, con la web recién abierta y sin sesión restaurada,
 * y `listUsers` devolvía `[]` en silencio («0 usuarios»).
 */
function useAdminUsers(enabled: boolean) {
  const [users, setUsers] = useState<FirebaseUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<LoadError | null>(null);
  // SSCA-FIX-1: guard de montaje para los setState tras await de refresh.
  const isMounted = useIsMounted();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listUsers();
      if (isMounted()) setUsers(result);
    } catch (err) {
      // PANADM-b1: el fallo se dice como fallo, con su código; no se deja la lista vieja.
      if (isMounted()) {
        setUsers([]);
        setError(toLoadError(err, 'No se ha podido leer la lista de usuarios.'));
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);
  return { users, loading, error, refresh };
}

/** SEC0-b2c-2: igual que `useAdminUsers`; el fallo ya no se confunde con «no hay». */
function usePendingRequests(enabled: boolean) {
  const [requests, setRequests] = useState<EditorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<LoadError | null>(null);
  // SSCA-FIX-1: guard de montaje para los setState tras await de refresh.
  const isMounted = useIsMounted();

  const refresh = useCallback(async () => {
    setLoading(true);
    setFailed(null);
    try {
      const result = await listPendingRequests();
      if (isMounted()) setRequests(result);
    } catch (err) {
      if (isMounted()) {
        setRequests([]);
        setFailed(toLoadError(err, 'No se han podido leer las solicitudes de editor.'));
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);
  return { requests, loading, failed, refresh };
}

// =============================================================================
// Helpers
// =============================================================================

/** Resalta coincidencias de búsqueda en texto */
function HighlightText({ text, query, t }: { text: string; query: string; t: AuthTokenSet }) {
  if (!query.trim()) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{
        background: `${t.primary}30`, borderBottom: `2px solid ${t.primary}`,
        padding: '0 1px', borderRadius: '2px',
      }}>
        {text.slice(idx, idx + query.length)}
      </span>
      {text.slice(idx + query.length)}
    </>
  );
}

/** Iniciales para avatar */
function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// =============================================================================
// Sub-component: RoleDropdown (custom con iconos inline)
// =============================================================================

interface RoleDropdownProps {
  uid: string;
  value: UserRole;
  disabled: boolean;
  onChange: (role: UserRole) => void;
  t: AuthTokenSet;
}

function RoleDropdown({ uid, value, disabled, onChange, t }: RoleDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const cfg = ROLE_CONFIG[value];
  const Icon = cfg.icon;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        data-testid={`role-dropdown-${uid}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '4px 8px', background: t.bgInput,
          border: `1px solid ${isOpen ? cfg.color : t.border}`,
          borderRadius: '6px', cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1, fontSize: '12px', color: t.text,
          fontFamily: "'JetBrains Mono', monospace", transition: 'border-color 0.15s',
        }}
      >
        <span style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '20px', height: '20px', borderRadius: '4px',
          background: `${cfg.color}20`, color: cfg.color,
        }}>
          <Icon size={12} strokeWidth={2} />
        </span>
        <span style={{ color: cfg.color, fontWeight: 600 }}>{cfg.label}</span>
        <ChevronDown size={12} style={{
          color: t.textMuted, transition: 'transform 0.15s',
          transform: isOpen ? 'rotate(180deg)' : 'none',
        }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: '4px',
          background: t.bgCard, border: `1px solid ${cfg.color}40`,
          borderRadius: '6px', overflow: 'hidden', zIndex: 50,
          minWidth: '180px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        }}>
          {ROLES.map(r => {
            const rc = ROLE_CONFIG[r];
            const RIcon = rc.icon;
            const isActive = r === value;
            return (
              <button
                key={r}
                data-testid={`role-option-${r}`}
                onClick={() => { onChange(r); setIsOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', padding: '8px 10px', border: 'none',
                  background: isActive ? `${rc.color}15` : 'transparent',
                  cursor: 'pointer', fontSize: '12px', color: isActive ? rc.color : t.text,
                  fontFamily: "'JetBrains Mono', monospace", textAlign: 'left',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = `${t.bgInput}`; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? `${rc.color}15` : 'transparent'; }}
              >
                <RIcon size={14} strokeWidth={2} style={{ color: rc.color }} />
                <span style={{ fontWeight: isActive ? 600 : 400 }}>{rc.label}</span>
                {r === 'editor' && (
                  <span style={{ marginLeft: 'auto', fontSize: '10px', color: t.textMuted }}>
                    {EDITOR_DOMAIN}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Sub-component: DurationSelector
// =============================================================================

interface DurationSelectorProps {
  value: RoleDuration;
  onChange: (d: RoleDuration) => void;
  t: AuthTokenSet;
}

function DurationSelector({ value, onChange, t }: DurationSelectorProps) {
  const durations = Object.entries(ROLE_DURATIONS) as [RoleDuration, typeof ROLE_DURATIONS[RoleDuration]][];
  // G1: capturar now antes del render del mapa — valor estable en el ciclo de render actual
  // eslint-disable-next-line react-hooks/purity
  const now = Date.now();
  return (
    <div data-testid="duration-selector" style={{ marginBottom: '16px' }}>
      <div style={{
        fontSize: '12px', color: t.textMuted, marginBottom: '6px',
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        Duración del rol Editor
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {durations.map(([key, meta]) => {
          const isActive = value === key;
          const expDate = meta.days !== null
            ? new Date(now + meta.days * 86400000).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })
            : null;
          return (
            <button
              key={key}
              data-testid={`duration-${key}`}
              onClick={() => onChange(key)}
              style={{
                padding: '6px 10px', borderRadius: '6px', fontSize: '12px',
                fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer',
                border: `1px solid ${isActive ? '#3b82f6' : t.border}`,
                background: isActive ? '#3b82f615' : 'transparent',
                color: isActive ? '#3b82f6' : t.text, fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: '2px', minWidth: '80px',
              }}
            >
              <span>{meta.label}</span>
              {expDate && (
                <span style={{ fontSize: '10px', color: t.textMuted, fontWeight: 400 }}>
                  → {expDate}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

// =============================================================================
// Panel resize constants
// =============================================================================

const PANEL_WIDTH_KEY = 'ptel-admin-panel-width';
const PANEL_MIN = 480;
const PANEL_MAX = () => Math.round(window.innerWidth * 0.95);

export function AdminPanel({ open, onOpenChange, theme = 'dark' }: AdminPanelProps) {
  const t = getAuthTokens(theme);
  const { isAdmin, userData } = useAuth();
  const { logDocument } = useAudit();
  // SEC0-b2c-2: las listas se piden al abrirse el panel, como en ModeratorPanel.
  const listsEnabled = open && isAdmin;
  const { users, loading, error, refresh } = useAdminUsers(listsEnabled);
  const {
    requests: pendingRequests, loading: pendingLoading, failed: pendingFailed, refresh: pendingRefresh,
  } = usePendingRequests(listsEnabled);
  // SSCA-FIX-1: guard de montaje para el setState tras await de executeRoleChange.
  const isMounted = useIsMounted();

  // --- Resize state ---
  const [panelWidth, setPanelWidth] = useState<number>(() => {
    const stored = localStorage.getItem(PANEL_WIDTH_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed)) return Math.min(Math.max(parsed, PANEL_MIN), PANEL_MAX());
    }
    return 560;
  });
  const isResizing = useRef(false);

  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const w = Math.min(Math.max(window.innerWidth - ev.clientX, PANEL_MIN), PANEL_MAX());
      setPanelWidth(w);
    };
    const onUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setPanelWidth(prev => { localStorage.setItem(PANEL_WIDTH_KEY, String(prev)); return prev; });
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const [filterRole, setFilterRole] = useState<FilterRole>('all');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<{
    type: 'confirm' | 'domain-warning';
    user: FirebaseUser;
    newRole: UserRole;
    selectedDuration: RoleDuration;
  } | null>(null);

  // --- Access denied ---
  if (!isAdmin) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" style={{ background: t.bgCard, color: t.text }}>
          <SheetHeader>
            <SheetTitle style={{ color: t.text }}>Acceso denegado</SheetTitle>
            <SheetDescription style={{ color: t.textSecondary }}>
              Solo los administradores pueden acceder a este panel.
            </SheetDescription>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    );
  }

  // --- Filtering ---
  const q = search.toLowerCase().trim();
  const filtered = users.filter(u => {
    if (filterRole !== 'all' && u.role !== filterRole) return false;
    if (q && !u.displayName.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
    return true;
  });

  const roleCounts = users.reduce((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // --- Handlers ---
  const handleRoleRequest = (user: FirebaseUser, newRole: UserRole) => {
    if (user.uid === userData?.uid) return;
    // Domain check for editor
    if (newRole === 'editor' && !user.email.endsWith(EDITOR_DOMAIN)) {
      setModal({ type: 'domain-warning', user, newRole, selectedDuration: '3m' });
      return;
    }
    // Admin promotion/demotion needs confirmation
    if (newRole === 'admin' || user.role === 'admin') {
      setModal({ type: 'confirm', user, newRole, selectedDuration: '3m' });
      return;
    }
    // Editor assignment needs duration selection
    if (newRole === 'editor') {
      setModal({ type: 'confirm', user, newRole, selectedDuration: '3m' });
      return;
    }
    void executeRoleChange(user, newRole);
  };

  const executeRoleChange = async (user: FirebaseUser, newRole: UserRole) => {
    try {
      await updateUserRole(user.uid, newRole);
      await logDocument('update', user.uid, '', { role: user.role }, { role: newRole });
      await refresh();
    } catch {
      alert('Error al cambiar el rol.');
    }
    if (isMounted()) setModal(null);
  };

  // --- Pending requests handlers ---
  const handleApproveRequest = async (requestId: string, duration: RoleDuration) => {
    if (!userData?.uid) return;
    await approveRequest(requestId, userData.uid, duration);
    // FB.08: Auditar aprobación de solicitud
    await logDocument('approve', requestId, '', null, { duration });
    await pendingRefresh();
    await refresh();
  };

  const handleRejectRequest = async (requestId: string, reason?: string) => {
    if (!userData?.uid) return;
    await rejectRequest(requestId, userData.uid, reason);
    // FB.08: Auditar rechazo de solicitud
    await logDocument('reject', requestId, '', null, { reason: reason || 'sin motivo' });
    await pendingRefresh();
  };

  const clearFilters = () => { setFilterRole('all'); setSearch(''); };

  // --- Filter pills config ---
  const pills: { key: FilterRole; label: string; icon: typeof Users; color: string; tooltip: string }[] = [
    { key: 'all', label: 'Todos', icon: Users, color: t.primary, tooltip: ROLE_TOOLTIPS.all },
    ...ROLES.map(r => ({ key: r as FilterRole, label: ROLE_CONFIG[r].label, icon: ROLE_CONFIG[r].icon, color: ROLE_CONFIG[r].color, tooltip: ROLE_TOOLTIPS[r] })),
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="admin-panel-root"
        style={{ background: t.bgCard, color: t.text, width: `${panelWidth}px`, maxWidth: '95vw', transition: isResizing.current ? 'none' : 'width 0.15s ease' }}
      >
        {/* Handle de redimensionado */}
        <div
          onMouseDown={startResize}
          title="Arrastra para ajustar el ancho"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '6px',
            cursor: 'ew-resize',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div style={{
            width: '3px',
            height: '48px',
            borderRadius: '2px',
            background: t.border,
            opacity: 0.5,
            transition: 'opacity 0.15s, background 0.15s',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; (e.currentTarget as HTMLElement).style.background = t.primary; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.5'; (e.currentTarget as HTMLElement).style.background = t.border; }}
          />
        </div>
        <SheetHeader>
          <SheetTitle style={{ color: t.text, fontFamily: "'Space Grotesk', sans-serif" }}>
            Panel de administración
          </SheetTitle>
          <SheetDescription style={{ color: t.textSecondary, lineHeight: 1.5 }}>
            Gestión de usuarios, roles y auditoría. Revisa y modifica los roles de los {users.length} usuario{users.length !== 1 ? 's' : ''} registrado{users.length !== 1 ? 's' : ''}, resuelve solicitudes pendientes y consulta el registro de acciones trazadas.
          </SheetDescription>
        </SheetHeader>

        <div style={{ padding: '0 16px 16px', overflowY: 'auto', flex: 1 }}>
          {/* --- Filter pills --- */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
            {pills.map(p => {
              const isActive = filterRole === p.key;
              const count = p.key === 'all' ? users.length : (roleCounts[p.key] || 0);
              const PIcon = p.icon;
              return (
                <button
                  key={p.key}
                  data-testid={`filter-${p.key}`}
                  onClick={() => setFilterRole(p.key)}
                  title={p.tooltip}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '4px 10px', borderRadius: '16px', fontSize: '12px',
                    fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer',
                    border: `1px solid ${isActive ? p.color : t.border}`,
                    background: isActive ? `${p.color}15` : 'transparent',
                    color: isActive ? p.color : t.textMuted, fontWeight: isActive ? 600 : 400,
                    transition: 'all 0.15s',
                  }}
                >
                  <PIcon size={13} strokeWidth={2} />
                  {p.label}
                  <span style={{
                    background: isActive ? `${p.color}25` : `${t.border}`,
                    padding: '0 5px', borderRadius: '8px', fontSize: '10px',
                    fontWeight: 700, minWidth: '18px', textAlign: 'center',
                  }}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* --- Search --- */}
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <Search size={14} style={{
              position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
              color: t.textMuted, pointerEvents: 'none',
            }} />
            <input
              data-testid="admin-search"
              type="text"
              placeholder="Buscar por nombre o correo…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '7px 32px 7px 32px', boxSizing: 'border-box',
                background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: '6px',
                color: t.text, fontSize: '12px', fontFamily: "'JetBrains Mono', monospace",
                outline: 'none',
              }}
            />
            {search && (
              <button
                data-testid="admin-search-clear"
                onClick={() => setSearch('')}
                style={{
                  position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: t.textMuted,
                  padding: 0, display: 'flex',
                }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* --- Pending Requests (FB.10.09) --- */}
          {pendingFailed && (
            <LoadErrorNotice
              testId="admin-pending-error"
              error={pendingFailed}
              what="solicitudes"
              onRetry={() => void pendingRefresh()}
              t={t}
            />
          )}
          <PendingRequests
            requests={pendingRequests}
            loading={pendingLoading}
            onApprove={handleApproveRequest}
            onReject={handleRejectRequest}
            t={t}
          />

          {/* --- Loading / Error --- */}
          {loading && (
            <div data-testid="admin-loading" style={{ textAlign: 'center', padding: '32px', color: t.textMuted }}>
              Cargando usuarios…
            </div>
          )}
          {error && (
            <LoadErrorNotice
              testId="admin-error"
              error={error}
              what="usuarios"
              onRetry={() => void refresh()}
              t={t}
            />
          )}

          {/* --- User Table --- */}
          {!loading && !error && filtered.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: "'JetBrains Mono', monospace" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: `1px solid ${t.border}`, color: t.textMuted, fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Usuario
                  </th>
                  <th style={{ textAlign: 'left', padding: '8px 6px', borderBottom: `1px solid ${t.border}`, color: t.textMuted, fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Rol
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((user) => {
                  const isSelf = user.uid === userData?.uid;
                  const rc = ROLE_CONFIG[user.role];
                  const expiry = describeExpiry(user.role, user.roleExpiresAt);
                  return (
                    <tr key={user.uid}>
                      <td style={{ padding: '10px 6px', borderBottom: `1px solid ${t.border}`, verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                          {/* Avatar */}
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '50%',
                            background: `${rc.color}20`, color: rc.color,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '11px', fontWeight: 700, flexShrink: 0,
                          }}>
                            {getInitials(user.displayName || user.email)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              <HighlightText text={user.displayName || '—'} query={q} t={t} />
                            </div>
                            <a
                              href={`mailto:${user.email}`}
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                fontSize: '11px', color: t.textMuted, textDecoration: 'none',
                                transition: 'color 0.15s',
                              }}
                              onMouseEnter={(e) => { e.currentTarget.style.color = t.primary; }}
                              onMouseLeave={(e) => { e.currentTarget.style.color = t.textMuted; }}
                            >
                              <Mail size={10} />
                              <HighlightText text={user.email} query={q} t={t} />
                            </a>
                            {/* PANADM-b1 DOP-0 (a): sin marca de verificación hasta E-CUENTAS
                                (la ficha no es fuente fiable; el dato real está en Firebase Auth). */}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '10px 6px', borderBottom: `1px solid ${t.border}`, verticalAlign: 'top' }}>
                        <RoleDropdown
                          uid={user.uid}
                          value={user.role}
                          disabled={isSelf}
                          onChange={(newRole) => handleRoleRequest(user, newRole)}
                          t={t}
                        />
                        {isSelf && (
                          <div style={{ fontSize: '10px', color: t.textMuted, marginTop: '4px' }}>
                            Tu cuenta
                          </div>
                        )}
                        {!isSelf && expiry && (
                          <div
                            data-testid={`expiry-${user.uid}`}
                            data-tone={expiry.tone}
                            style={{
                              fontSize: '10px', marginTop: '4px',
                              fontWeight: expiry.tone === 'muted' ? 400 : 600,
                              color: expiry.tone === 'error' ? t.error : expiry.tone === 'warn' ? AMBER : t.textMuted,
                            }}
                          >
                            {expiry.text}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* --- Empty state --- */}
          {!loading && !error && filtered.length === 0 && users.length > 0 && (
            <div data-testid="admin-empty" style={{
              textAlign: 'center', padding: '32px', color: t.textMuted, fontSize: '13px',
            }}>
              <Search size={24} style={{ opacity: 0.3, marginBottom: '8px' }} />
              <div>Sin resultados para esta búsqueda</div>
              <button
                onClick={clearFilters}
                style={{
                  background: 'none', border: 'none', color: t.primary,
                  cursor: 'pointer', fontSize: '12px', marginTop: '8px',
                  fontFamily: "'JetBrains Mono', monospace", textDecoration: 'underline',
                }}
              >
                Limpiar filtros
              </button>
            </div>
          )}

          {/* --- Footer --- */}
          {!loading && !error && (
            <div style={{
              marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${t.border}`,
              fontSize: '11px', color: t.textMuted, textAlign: 'center',
            }}>
              {(filterRole !== 'all' || q) ? (
                <span>{filtered.length} de {users.length} usuarios</span>
              ) : (
                <span>Cambios de rol se registran en auditoría</span>
              )}
            </div>
          )}

          {/* --- Audit Log (FB.11.03) --- */}
          <div style={{
            marginTop: '24px', paddingTop: '16px',
            borderTop: `1px solid ${t.border}`,
          }}>
            <AuditLogViewer t={t} />
          </div>
        </div>

        {/* --- Modal: Domain Warning --- */}
        {modal?.type === 'domain-warning' && (
          <div style={{
            position: 'fixed', inset: 0, background: t.backdrop, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: '12px',
              padding: '24px', maxWidth: '380px', width: '90%',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 600, fontSize: '15px', fontFamily: "'Space Grotesk', sans-serif", color: t.text }}>
                  Dominio no autorizado
                </span>
              </div>
              <p style={{ fontSize: '13px', color: t.textSecondary, lineHeight: 1.5, margin: '0 0 16px' }}>
                El rol <strong style={{ color: ROLE_CONFIG.editor.color }}>Editor</strong> normalmente requiere
                correo <strong>{EDITOR_DOMAIN}</strong>. El usuario <strong>{modal.user.displayName}</strong> tiene{' '}
                <em>{modal.user.email}</em>.
              </p>
              <p style={{ fontSize: '13px', color: t.text, lineHeight: 1.5, margin: '0 0 16px' }}>
                ¿Deseas asignarle Editor igualmente?
              </p>
              <DurationSelector
                value={modal.selectedDuration}
                onChange={(d) => setModal(prev => prev ? { ...prev, selectedDuration: d } : null)}
                t={t}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  data-testid="modal-cancel"
                  onClick={() => setModal(null)}
                  style={{
                    padding: '8px 16px', background: 'transparent', border: `1px solid ${t.border}`,
                    borderRadius: '6px', color: t.textSecondary, cursor: 'pointer', fontSize: '13px',
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  Cancelar
                </button>
                <button
                  data-testid="modal-confirm-domain"
                  onClick={() => void executeRoleChange(modal.user, modal.newRole)}
                  style={{
                    padding: '8px 16px', background: ROLE_CONFIG.editor.color,
                    border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  Confirmar igualmente
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- Modal: Confirm Role Change --- */}
        {modal?.type === 'confirm' && (
          <div style={{
            position: 'fixed', inset: 0, background: t.backdrop, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: '12px',
              padding: '24px', maxWidth: '380px', width: '90%',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <AlertTriangle size={20} style={{ color: '#f59e0b' }} />
                <span style={{ fontWeight: 600, fontSize: '15px', fontFamily: "'Space Grotesk', sans-serif", color: t.text }}>
                  Confirmar cambio de rol
                </span>
              </div>
              <p style={{ fontSize: '13px', color: t.textSecondary, lineHeight: 1.5, margin: '0 0 16px' }}>
                ¿Cambiar a <strong>{modal.user.displayName}</strong> de{' '}
                <strong style={{ color: ROLE_CONFIG[modal.user.role].color }}>{ROLE_CONFIG[modal.user.role].label}</strong>{' '}
                a <strong style={{ color: ROLE_CONFIG[modal.newRole].color }}>{ROLE_CONFIG[modal.newRole].label}</strong>?
                {modal.newRole === 'admin' && (
                  <span style={{ display: 'block', marginTop: '8px', color: '#f59e0b', fontSize: '12px' }}>
                    Los administradores tienen acceso total al sistema.
                  </span>
                )}
              </p>
              {modal.newRole === 'editor' && (
                <DurationSelector
                  value={modal.selectedDuration}
                  onChange={(d) => setModal(prev => prev ? { ...prev, selectedDuration: d } : null)}
                  t={t}
                />
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  data-testid="modal-cancel"
                  onClick={() => setModal(null)}
                  style={{
                    padding: '8px 16px', background: 'transparent', border: `1px solid ${t.border}`,
                    borderRadius: '6px', color: t.textSecondary, cursor: 'pointer', fontSize: '13px',
                    fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  Cancelar
                </button>
                <button
                  data-testid="modal-confirm"
                  onClick={() => void executeRoleChange(modal.user, modal.newRole)}
                  style={{
                    padding: '8px 16px', background: ROLE_CONFIG[modal.newRole].color,
                    border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer',
                    fontSize: '13px', fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif",
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
